import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { Contact, DataProduct, DataVendor, DrmEntityBackend } from 'v2/models';
import noAssociationsImage from 'v2/images/noGenericAssociations.svg';
import {
  SortPrimaryToTop,
  selectPrimary,
  unSelectPrimary,
} from 'v2/utils/associationUtils';
import restFactory from 'v2/utils/restFactory';
import CreateAssociationModal from './CreateAssociationModal';
import AssociationItem from './AssociationItem';
import AssociationTabList from '../AssociationTab/AssociationTabList';
import styles from './associationItem.module.scss';
import { Spinner } from 'react-bootstrap';
import classNames from 'classnames';
import GenericAssociationTabContent from './GenericAssociationTabContent';
import { TranslationKeys, PostProcessKeys } from 'translations';
import { useTranslation } from 'react-i18next';

interface AssociationTabProps {
  entity: DRMEntity;
  refetch: () => void;
}

interface AssociationsData {
  data_vendor_associations?: DataVendor[];
  data_product_associations?: DataProduct[];
  contact_associations?: Contact[];
}

export interface DRMEntity {
  id: number;
  name: string;
  is_editable: boolean;
  is_deletable: boolean;
  type: DrmEntityBackend;
}

const GenericAssociationTab = ({ entity, refetch }: AssociationTabProps) => {
  const { t } = useTranslation();
  const [showAddModal, setShowAddModal] = useState(false);
  const isContactEntity = entity.type === DrmEntityBackend.CONTACT;
  const {
    data: dataVendors,
    refetch: loadDataVendors,
    isLoading: isDataVendorsLoading,
  } = useQuery<DataVendor[]>(
    [`${entity.type}AssociationTab`, 'dataVendors'],
    async () =>
      await restFactory.get<DataVendor[]>(
        `/dj/api/v1/${entity.type}/${entity.id}/data_vendors/`
      ),
    { cacheTime: 0 }
  );

  const {
    data: dataProducts,
    refetch: loadDataProducts,
    isLoading: isDataProductsLoading,
  } = useQuery<DataProduct[]>(
    ['ContactAssociationTab', 'dataProducts'],
    async () =>
      await restFactory.get<DataProduct[]>(
        `/dj/api/v1/${entity.type}/${entity.id}/data_products/`
      ),
    { cacheTime: 0, enabled: isContactEntity }
  );

  const {
    data: contacts,
    refetch: loadContacts,
    isLoading: isContactsLoading,
  } = useQuery<Contact[]>(
    ['DatasetAssociationTab', 'contacts'],
    async () =>
      await restFactory.get<Contact[]>(
        `/dj/api/v1/${entity.type}/${entity.id}/contacts/`
      ),
    { cacheTime: 0, enabled: !isContactEntity }
  );

  const { mutate: updateAssociation, isLoading: isUpdatingAssociation } =
    useMutation({
      mutationFn: async (data: AssociationsData) =>
        await restFactory.patch(
          `/dj/api/v1/${entity.type}/${entity.id}/`,
          data
        ),
      onSuccess: () => {
        loadDataVendors();
        isContactEntity && loadDataProducts();
        !isContactEntity && loadContacts();
        refetch();
        toast.success('Association updated');
      },
      onError: () => {
        toast.error('Error updating association');
      },
    });

  const selectPrimaryGeneric = ({
    id,
    type,
    isSelect,
  }: {
    id: number;
    type: DrmEntityBackend;
    isSelect: boolean;
  }): void => {
    switch (type) {
      case DrmEntityBackend.DATASET: {
        const data = (dataProducts || []).map(
          isSelect ? selectPrimary(id) : unSelectPrimary(id)
        );
        return updateAssociation({
          data_product_associations: data as DataProduct[],
        });
      }
      case DrmEntityBackend.DATA_VENDOR: {
        const data = (dataVendors || []).map(
          isSelect ? selectPrimary(id) : unSelectPrimary(id)
        );
        return updateAssociation({
          data_vendor_associations: data as DataVendor[],
        });
      }
      case DrmEntityBackend.CONTACT: {
        const data = (contacts || []).map(
          isSelect ? selectPrimary(id) : unSelectPrimary(id)
        );
        return updateAssociation({ contact_associations: data as Contact[] });
      }
      default:
        break;
    }
  };

  const deleteAssociation = ({
    id,
    type,
  }: {
    id: number;
    type: DrmEntityBackend;
  }) => {
    switch (type) {
      case DrmEntityBackend.DATASET:
        return updateAssociation({
          data_product_associations: dataProducts?.filter((dp) => dp.id !== id),
        });
      case DrmEntityBackend.DATA_VENDOR:
        return updateAssociation({
          data_vendor_associations: dataVendors?.filter((dv) => dv.id !== id),
        });
      case DrmEntityBackend.CONTACT:
        return updateAssociation({
          contact_associations: contacts?.filter((dv) => dv.id !== id),
        });
      default:
        break;
    }
  };

  const sortedDataVendors = SortPrimaryToTop(dataVendors);

  const sortedDataProducts = isContactEntity
    ? SortPrimaryToTop(dataProducts)
    : [];

  const sortedContacts = !isContactEntity ? SortPrimaryToTop(contacts) : [];

  if (isDataVendorsLoading || isDataProductsLoading || isContactsLoading) {
    return (
      <div className={classNames(styles.loading)} aria-label="loading-spinner">
        <Spinner animation="border" />
      </div>
    );
  }

  const handleAddModalClose = (refresh?: boolean) => {
    if (refresh) {
      loadDataVendors();
      isContactEntity && loadDataProducts();
      !isContactEntity && loadContacts();
      refetch();
    }
    setShowAddModal(false);
  };

  const buildAssociations = (
    associationType: DrmEntityBackend,
    associationTitle: string,
    associationItems: (DataVendor | Contact | DataProduct)[]
  ) => {
    return {
      name: associationTitle,
      items: associationItems.map((item) => (
        <AssociationItem
          key={item.id}
          is_primary={item.is_primary}
          entity={entity}
          onDelete={() =>
            deleteAssociation({
              id: item.id,
              type: associationType,
            })
          }
          onUnselectPrimary={() =>
            selectPrimaryGeneric({
              id: item.id,
              type: associationType,
              isSelect: false,
            })
          }
          onSelectPrimary={() =>
            selectPrimaryGeneric({
              id: item.id,
              type: associationType,
              isSelect: true,
            })
          }
          isDeleting={isUpdatingAssociation}
        >
          <GenericAssociationTabContent
            associationParentName={entity.name}
            associationItem={item}
          />
        </AssociationItem>
      )),
    };
  };

  const allAssociations = [
    buildAssociations(
      DrmEntityBackend.DATA_VENDOR,
      t(TranslationKeys.company_other, {
        postProcess: PostProcessKeys.TitleCase,
      }),
      sortedDataVendors
    ),
    ...(isContactEntity
      ? [
          buildAssociations(
            DrmEntityBackend.DATASET,
            t(TranslationKeys.dataProduct_other, {
              postProcess: PostProcessKeys.TitleCase,
            }),
            sortedDataProducts
          ),
        ]
      : [
          buildAssociations(
            DrmEntityBackend.CONTACT,
            t(TranslationKeys.contact_other, {
              postProcess: PostProcessKeys.TitleCase,
            }),
            sortedContacts
          ),
        ]),
  ].filter((group) => group.items.length > 0);

  return (
    <>
      <AssociationTabList
        groups={allAssociations}
        buttonLabel="Add An Association"
        onButtonClick={() => setShowAddModal(true)}
        image={noAssociationsImage}
        showButton={entity.is_editable}
      />
      <CreateAssociationModal
        show={showAddModal}
        entity={entity}
        onClose={handleAddModalClose}
      />
    </>
  );
};

export default GenericAssociationTab;
